import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FlaskConical, X } from "lucide-react";
import Seo from "@/components/Seo";
import ArticleBody from "@/components/ArticleBody";
import { SiteButton } from "@/components/brand/SiteButton";
import { Container, Section, SectionHeading } from "@/components/layout/Section";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ROUTE_SEO } from "@/config/seoData";

const FILTERS = [
  { key: "", label: "All work" },
  { key: "concept", label: "Concept projects" },
  { key: "case-study", label: "Case studies" },
];

const Work = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const visible = (items || []).filter((p) => !filter || p.kind === filter);

  return (
    <>
      <Seo {...ROUTE_SEO.work} />
      <Section testid="work-page" className="pt-32 sm:pt-40">
        <Container className="space-y-12">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-4" data-testid="work-heading">
              <p className="eyebrow">Work</p>
              <h1 className="font-display text-3xl font-bold tracking-tight text-brand-snow sm:text-4xl lg:text-5xl">
                Proof of craft, honestly labelled
              </h1>
              <p className="max-w-2xl text-base leading-relaxed text-brand-mist">
                Concept projects and demonstrations are in-house explorations — not client engagements. Case studies are presented with the client's involvement and consent.
              </p>
            </div>
            <p className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full border border-brand-azure/30 bg-brand-azure/10 px-4 py-2 text-xs font-medium text-brand-azure" data-testid="work-disclaimer">
              <FlaskConical size={13} /> Labels tell you exactly what each piece is
            </p>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="work-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`work-filter-${f.key || "all"}`}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                  filter === f.key
                    ? "border-brand-emerald bg-brand-emerald/15 text-brand-emerald"
                    : "border-brand-mist/20 text-brand-mist hover:border-brand-emerald/40 hover:text-brand-snow"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {items === null && <p className="text-sm text-brand-mist">Loading…</p>}
          {items !== null && visible.length === 0 && (
            <p className="text-sm text-brand-mist" data-testid="work-empty">Nothing published in this view yet.</p>
          )}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="work-grid">
            {visible.map((p, i) => (
              <motion.button
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 2) * 0.08 }}
                onClick={() => setOpen(p)}
                data-testid={`work-card-${p.slug}`}
                className="group overflow-hidden rounded-xl border border-brand-mist/12 bg-brand-charcoal text-left transition-colors duration-300 hover:border-brand-emerald/30"
              >
                {p.image && (
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.image} alt={`${p.title} visual`} loading="lazy" decoding="async" width="800" height="450" className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-[1.04]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal via-brand-charcoal/20 to-transparent" />
                    <span className={cn(
                      "absolute left-4 top-4 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider backdrop-blur-sm",
                      p.kind === "case-study" ? "border-brand-emerald/40 bg-brand-black/70 text-brand-emerald" : "border-brand-azure/40 bg-brand-black/70 text-brand-azure"
                    )}>
                      {p.label}
                    </span>
                  </div>
                )}
                <div className="space-y-3 p-6">
                  <h2 className="font-display text-xl font-semibold text-brand-snow">{p.title}</h2>
                  <p className="text-sm leading-relaxed text-brand-mist">{p.summary}</p>
                  <ul className="flex flex-wrap gap-2 pt-1">
                    {(p.tags || []).map((t) => (
                      <li key={t} className="rounded-full border border-brand-mist/15 px-3 py-1 text-[11px] text-brand-mist">{t}</li>
                    ))}
                  </ul>
                </div>
              </motion.button>
            ))}
          </div>

          <div className="rounded-2xl border border-brand-emerald/20 bg-brand-charcoal p-10 text-center" data-testid="work-cta">
            <h2 className="mx-auto max-w-xl font-display text-2xl font-bold text-brand-snow sm:text-3xl">Your project could set the standard here</h2>
            <div className="mt-8 flex justify-center">
              <SiteButton to="/contact?source=work" testid="work-cta-contact" size="lg">Start a conversation</SiteButton>
            </div>
          </div>
        </Container>
      </Section>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-brand-black/70 backdrop-blur-sm sm:items-center sm:p-6" onClick={() => setOpen(null)} data-testid="work-detail-modal">
          <div className="max-h-[88vh] w-full max-w-2xl space-y-5 overflow-y-auto rounded-t-2xl border border-brand-mist/15 bg-brand-charcoal p-8 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className={cn("eyebrow", open.kind === "case-study" && "text-brand-azure")}>{open.label}</span>
                <h2 className="mt-2 font-display text-2xl font-bold text-brand-snow">{open.title}</h2>
              </div>
              <button onClick={() => setOpen(null)} aria-label="Close" data-testid="work-detail-close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
            </div>
            {open.body ? <ArticleBody content={open.body} testid="work-detail-body" /> : <p className="text-sm text-brand-mist">{open.summary}</p>}
            <div className="pt-2">
              <SiteButton to="/contact?source=work" testid="work-detail-cta" onClick={() => setOpen(null)}>Discuss a project like this</SiteButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Work;
